// AccountPage — "Account settings" at /account, rendered in the shell's main-content like the
// other sidebar tabs. A second, page-level sidebar (à la Jira's Personal settings) switches
// between sections; the selected one lives in ?section= so it survives a refresh.
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAuthOptions } from '../../utils/authOptions';
import { isMobileDevice } from '../../utils/device';
import PermissionGate from '../PermissionGate/PermissionGate';
import UserManagement from '../UserManagement/UserManagement';
import UserAccess from '../UserAccess/UserAccess';
import BiometricLoginToggle from './BiometricLoginToggle';
import SignInMethods from './SignInMethods';
import './AccountPage.css';

const SECTIONS = [
  // `needsSms`: Sign-in methods shows nothing until the owner turns text messages on.
  { id: 'sign-in', label: 'Sign-in methods', icon: 'fa-solid fa-key', needsSms: true, Component: SignInMethods },
  // `mobileOnly`: phones and tablets, by the same device check that decides whether
  // biometric unlock is offered at all (not the window width) - irrelevant on the web.
  { id: 'biometrics', label: 'Biometric unlock', icon: 'fa-solid fa-fingerprint', mobileOnly: true, Component: BiometricLoginToggle },
  // RDV Systems staff only (Cognito group Admin), desktop only like the /user-management
  // route it replaced. `wide`: a table needs the full width, not the 760px settings column.
  {
    id: 'user-management',
    label: 'User management',
    icon: 'fa-solid fa-users-gear',
    group: 'Admin',
    desktopOnly: true,
    wide: true,
    Component: () => <UserManagement embedded />,
  },
  // Who can open which project (projectusers), across every project: same audience.
  {
    id: 'user-access',
    label: 'User access',
    icon: 'fa-solid fa-user-lock',
    group: 'Admin',
    desktopOnly: true,
    wide: true,
    Component: UserAccess,
  },
];

// The same test as PermissionGate's `group` (case-insensitive).
const inGroup = (permissions, group) =>
  (permissions?.groups || []).some((g) => String(g).trim().toLowerCase() === group.toLowerCase());

const AccountPage = () => {
  const [params, setParams] = useSearchParams();
  const { permissions, loading } = useUserPermissions();
  const options = useAuthOptions();
  const isMobile = useIsMobile();
  // Sections this page and device can show; a section's Cognito group is checked on top -
  // by PermissionGate on its nav item, and here for a deep link (?section=).
  const available = (s) => (!s.needsSms || options?.sms === true)
    && (!s.desktopOnly || !isMobile)
    && (!s.mobileOnly || isMobileDevice());
  const allowed = (s) => available(s) && (!s.group || inGroup(permissions, s.group));
  const visible = SECTIONS.filter(allowed);
  // A section is chosen only once permissions and the SMS switch have loaded: a deep link
  // to a gated section isn't bounced while they load, and the page doesn't flick from one
  // section to another. Then a section the person can't see falls back to the first one -
  // or to none: on the web, with text messages off, only Admins have a section.
  const ready = !loading && options !== null;
  const requested = SECTIONS.find((s) => s.id === params.get('section'));
  const active = !ready ? null : (requested && allowed(requested) ? requested : visible[0] || null);
  const Active = active?.Component;

  return (
    <div className="account-page">
      <nav className="account-page__nav" aria-label="Account settings">
        <div className="account-page__nav-head">
          <Link to="/projects" className="account-page__back" aria-label="Back to projects">
            <i className="fas fa-arrow-left" aria-hidden="true"></i>
          </Link>
          <h2 className="account-page__nav-title">Account settings</h2>
        </div>
        <ul className="account-page__nav-list">
          {SECTIONS.filter(available).map((s) => {
            const item = (
              <li key={s.id}>
                <button
                  type="button"
                  className={`account-page__nav-item${s.id === active?.id ? ' is-active' : ''}`}
                  aria-current={s.id === active?.id ? 'page' : undefined}
                  onClick={() => setParams({ section: s.id }, { replace: true })}
                >
                  <i className={s.icon} aria-hidden="true"></i>
                  <span>{s.label}</span>
                </button>
              </li>
            );
            return s.group ? (
              <PermissionGate key={s.id} group={s.group} showLoading={false}>{item}</PermissionGate>
            ) : item;
          })}
        </ul>
      </nav>

      <main className="account-page__content">
        {active ? (
          <div className={`account-page__column${active.wide ? ' account-page__column--wide' : ''}`}>
            <p className="account-page__crumbs">Account settings / {active.label}</p>
            <h2 className="account-page__title">{active.label}</h2>
            <div className="account-page__body">
              <Active />
            </div>
          </div>
        ) : ready ? (
          <div className="account-page__column">
            <h2 className="account-page__title">Account settings</h2>
            <p className="account-page__empty">There&apos;s nothing to set up here yet.</p>
          </div>
        ) : (
          <div className="account-page__column" aria-busy="true" />
        )}
      </main>
    </div>
  );
};

export default AccountPage;
